import { Link } from 'react-router';
import { addDays, fmtLong, fmtRange, formatDays, leaveYearLabel, leaveYearOf } from '../lib/dates';
import { ACTIVE_STATUSES, balanceFor, canDecide, displayType, isNonWorking, partOnDay, partSuffix } from '../lib/leave';
import { useCtx, type Ctx } from '../store/useCtx';
import type { LeaveRequest } from '../types';
import { Avatar, StatusChip, plural, typeStyle } from '../ui/bits';
import { useDialogs } from '../ui/dialogs';
import { Icon } from '../ui/Icon';

function LeaveRow({ req, ctx }: { req: LeaveRequest; ctx: Ctx }) {
  const { openDetail } = useDialogs();
  const t = displayType(req, ctx.me, ctx);
  const part = partSuffix(req);
  return (
    <li className="row">
      <span className="type-dot" style={typeStyle(t)}><Icon name={t.icon} size={16} /></span>
      <div className="row-main">
        <button type="button" className="link-btn" onClick={() => openDetail(req.id)}>{t.label}</button>
        <span className="muted">{fmtRange(req.start, req.end)}{part ? ` · ${part}` : ''}</span>
      </div>
      <StatusChip status={req.status} />
    </li>
  );
}

export function Dashboard() {
  const ctx = useCtx();
  const { openRequest, openDetail } = useDialogs();
  const { me, today, settings } = ctx;
  const nameOf = (r: LeaveRequest) => ctx.userById.get(r.userId)?.name ?? '';
  const activeOwner = (r: LeaveRequest) => ctx.userById.get(r.userId)?.active;

  const ly = leaveYearOf(today, settings.leaveYearStartMonth);
  const bal = balanceFor(me, ly, ctx);
  const pct = (n: number) => (bal.allowance > 0 ? Math.max(0, Math.min(100, (n / bal.allowance) * 100)) : 0);

  const waiting = ctx.requests.filter((r) => r.status === 'pending' && canDecide(me, r, ctx));

  const nonWorking = isNonWorking(today, ctx.bh);
  const awayToday = nonWorking
    ? []
    : ctx.requests
        .filter((r) => r.status === 'approved' && partOnDay(r, today) && activeOwner(r))
        .sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'en-GB'));

  // Colleagues' approved leave in the next 30 days. Sick leave is never listed.
  const horizon = addDays(today, 30);
  const comingUp = ctx.requests
    .filter((r) => r.status === 'approved' && r.typeId !== 'sick' && r.userId !== me.id && r.start > today && r.start <= horizon && activeOwner(r))
    .sort((a, b) => a.start.localeCompare(b.start))
    .slice(0, 6);

  const mineUpcoming = ctx.requests
    .filter((r) => r.userId === me.id && ACTIVE_STATUSES.includes(r.status) && r.end >= today)
    .sort((a, b) => a.start.localeCompare(b.start));
  const mineHistory = ctx.requests
    .filter((r) => r.userId === me.id && !mineUpcoming.includes(r))
    .sort((a, b) => b.start.localeCompare(a.start))
    .slice(0, 5);

  const nextBankHolidays = [...ctx.bh.entries()].filter(([d]) => d >= today).sort(([a], [b]) => a.localeCompare(b)).slice(0, 3);
  const noHR = !ctx.users.some((u) => u.role === 'hr' && u.active);

  return (
    <>
      {noHR && (
        <div className="banner banner-warn" role="note">
          <Icon name="alert" />
          <div>
            <strong>Setup step needed:</strong> nobody has the HR role yet, so nobody can approve leave or use the Admin area.
            Add one from the backend folder with <code>manage.py add_user --role hr</code> (or set a person's role to HR in the database). See the README.
          </div>
        </div>
      )}

      {waiting.length > 0 && (
        <div className="banner" role="note">
          <Icon name="check-circle" />
          <div><strong>{plural(waiting.length, 'request')}</strong> waiting for your decision.</div>
          <Link className="btn btn-sm" to="/approvals">Review</Link>
        </div>
      )}

      <div className="dash-grid">
        <section className="card balance" aria-labelledby="bal-title">
          <header className="card-head">
            <h2 id="bal-title">Your holiday</h2>
            <span className="muted">Leave year {leaveYearLabel(ly, settings.leaveYearStartMonth)}</span>
          </header>
          <p className="big"><strong>{formatDays(bal.remaining)}</strong> <span>day{bal.remaining === 1 ? '' : 's'} remaining</span></p>
          <div className="bar" aria-hidden="true">
            <span className="bar-approved" style={{ width: `${pct(bal.approved)}%` }} />
            <span className="bar-pending" style={{ width: `${Math.min(pct(bal.pending), 100 - pct(bal.approved))}%` }} />
          </div>
          <dl className="stats">
            <div><dt>Allowance</dt><dd>{formatDays(bal.allowance)}</dd></div>
            <div><dt><span className="key key-approved" />Approved</dt><dd>{formatDays(bal.approved)}</dd></div>
            <div><dt><span className="key key-pending" />Pending</dt><dd>{formatDays(bal.pending)}</dd></div>
          </dl>
          <button type="button" className="btn" onClick={() => openRequest()}>
            <Icon name="plus" size={18} />
            Request leave
          </button>
        </section>

        <section className="card" aria-labelledby="away-title">
          <header className="card-head"><h2 id="away-title">Away today</h2><span className="muted">{fmtLong(today)}</span></header>
          {nonWorking ? (
            <p className="empty">{ctx.bh.has(today) ? `It's a bank holiday: ${ctx.bh.get(today)}.` : "It's the weekend."}</p>
          ) : awayToday.length ? (
            <ul className="list">
              {awayToday.map((r) => {
                const t = displayType(r, me, ctx);
                const p = partOnDay(r, today);
                return (
                  <li key={r.id} className="row">
                    <Avatar name={nameOf(r)} photoUrl={ctx.userById.get(r.userId)?.photoUrl} />
                    <div className="row-main">
                      <button type="button" className="link-btn" onClick={() => openDetail(r.id)}>{nameOf(r)}</button>
                      <span className="muted">{t.label}{p === 'am' ? ' · morning' : p === 'pm' ? ' · afternoon' : ''}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="empty">Everyone is in today.</p>
          )}
        </section>

        <section className="card" aria-labelledby="up-title">
          <header className="card-head"><h2 id="up-title">Your leave</h2><Link to="/year" className="link">My year</Link></header>
          {mineUpcoming.length ? (
            <ul className="list">{mineUpcoming.map((r) => <LeaveRow key={r.id} req={r} ctx={ctx} />)}</ul>
          ) : (
            <p className="empty">Nothing booked. Time to plan a break?</p>
          )}
          {mineHistory.length > 0 && (
            <>
              <h3 className="subhead">Earlier</h3>
              <ul className="list list-quiet">{mineHistory.map((r) => <LeaveRow key={r.id} req={r} ctx={ctx} />)}</ul>
            </>
          )}
        </section>

        <section className="card" aria-labelledby="soon-title">
          <header className="card-head"><h2 id="soon-title">Coming up</h2><span className="muted">Next 30 days</span></header>
          {comingUp.length ? (
            <ul className="list">
              {comingUp.map((r) => (
                <li key={r.id} className="row">
                  <Avatar name={nameOf(r)} photoUrl={ctx.userById.get(r.userId)?.photoUrl} />
                  <div className="row-main">
                    <button type="button" className="link-btn" onClick={() => openDetail(r.id)}>{nameOf(r)}</button>
                    <span className="muted">{displayType(r, me, ctx).label} · {fmtRange(r.start, r.end)}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="empty">No colleagues are booked off in the next 30 days.</p>
          )}
          {nextBankHolidays.length > 0 && (
            <>
              <h3 className="subhead">Bank holidays</h3>
              <ul className="list list-quiet">
                {nextBankHolidays.map(([d, name]) => (
                  <li key={d} className="row">
                    <span className="type-dot bh-dot"><Icon name="flag" size={16} /></span>
                    <div className="row-main"><span>{name}</span><span className="muted">{fmtLong(d)}</span></div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>
    </>
  );
}
